import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextAreaField,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Camera, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { CopyField, StatusBadge } from '../../components/Common';

interface Status {
  configured: boolean;
  appId: string | null;
  graphVersion: string | null;
  webhookVerifyTokenSet: boolean;
  redirectUri: string | null;
  webhookPath: string;
}
interface Account {
  id: string;
  igUserId: string;
  username: string;
  name: string | null;
  method: 'oauth' | 'manual_token';
  status: 'active' | 'needs_reauth' | 'disconnected';
  tokenExpiresAt: string | null;
  createdAt: string;
}
type Trigger = 'comment' | 'dm_keyword' | 'story_reply';
interface Rule {
  id: string;
  accountId: string;
  name: string;
  trigger: Trigger;
  mediaId: string | null;
  keywords: string[];
  matchType: 'contains' | 'exact';
  replyText: string;
  commentReplyText: string | null;
  handoff: boolean;
  enabled: boolean;
}
interface Activity {
  id: string;
  rule: string;
  contactId: string;
  outcome: string;
  createdAt: string;
}

const TRIGGER_LABEL: Record<Trigger, string> = {
  comment: 'Comment on a post',
  dm_keyword: 'Keyword in a message',
  story_reply: 'Reply to your story',
};

const OAUTH_ERRORS: Record<string, string> = {
  state_invalid: 'The connection attempt expired or came from another session. Try again.',
  access_denied: 'Instagram did not grant access.',
  invalid_code: 'The authorization code was invalid or expired. Try again.',
  account_taken: 'That Instagram account is connected to another organization.',
  meta_error: 'Instagram returned an error. Try again in a moment.',
  failed: 'Could not connect the account.',
};

const OUTCOME_LABEL = (outcome: string) =>
  outcome === 'sent'
    ? 'Replied'
    : outcome.startsWith('skipped:')
      ? `Skipped (${outcome.slice(8).replace(/_/g, ' ')})`
      : outcome.startsWith('sent:')
        ? 'Replied (public reply failed)'
        : outcome.replace(/[:_]/g, ' ');

/** Instagram accounts and automatic replies: comment-to-DM, keywords and story replies. */
export function InstagramPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const [params, setParams] = useSearchParams();
  const status = useLoader(() => api.get<Status>('/instagram/status'), tenantId);
  const accounts = useLoader(
    () => api.get<{ accounts: Account[] }>('/instagram/accounts').then((r) => r.accounts),
    tenantId,
  );
  const rules = useLoader(
    () => api.get<{ rules: Rule[] }>('/instagram/rules').then((r) => r.rules),
    tenantId,
  );
  const activity = useLoader(
    () => api.get<{ activity: Activity[] }>('/instagram/rules/activity').then((r) => r.activity),
    tenantId,
  );
  const manage = can('channels:manage');
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);
  const [connecting, setConnecting] = useState(false);

  const connectedName = params.get('connected');
  const oauthError = params.get('error');
  useEffect(() => {
    if (!connectedName && !oauthError) return;
    const t = setTimeout(() => setParams({}, { replace: true }), 8000);
    return () => clearTimeout(t);
  }, [connectedName, oauthError, setParams]);

  const startOauth = async () => {
    setConnecting(true);
    try {
      const r = await api.post<{ url: string }>('/instagram/oauth/start');
      window.location.assign(r.url);
    } catch (err) {
      toast.error(errorMessage(err));
      setConnecting(false);
    }
  };

  const disconnect = async (a: Account) => {
    const ok = await confirm({
      title: `Disconnect @${a.username}?`,
      message:
        'Conversation history stays. The stored access token is deleted and the account’s rules are turned off.',
      confirmLabel: 'Disconnect',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/instagram/accounts/${a.id}`);
      toast.success('Account disconnected');
      void accounts.reload();
      void rules.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const toggle = async (r: Rule) => {
    try {
      await api.patch(`/instagram/rules/${r.id}`, { ...r, enabled: !r.enabled });
      void rules.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async (r: Rule) => {
    const ok = await confirm({
      title: `Delete “${r.name}”?`,
      message: 'The rule stops replying immediately. Past activity is kept.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/instagram/rules/${r.id}`);
      void rules.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if ((status.loading && !status.data) || (accounts.loading && !accounts.data)) {
    return <Spinner size="lg" label="Loading" />;
  }
  if (status.error || !status.data || accounts.error || !accounts.data) {
    return <Alert tone="critical">{status.error ?? accounts.error ?? 'Failed to load'}</Alert>;
  }
  const cfg = status.data;
  const list = accounts.data;
  const active = list.filter((a) => a.status === 'active');

  return (
    <div className="stack-lg">
      <PageHeader
        title="Instagram"
        description="Answer Instagram messages in the Inbox, and reply automatically to comments, keywords and story replies."
      />

      {connectedName && <Alert tone="success">Connected @{connectedName}.</Alert>}
      {oauthError && (
        <Alert tone="critical">
          {OAUTH_ERRORS[oauthError] ?? 'Could not connect the account.'}
        </Alert>
      )}
      {!cfg.configured && (
        <Alert tone="warning" title="Pending configuration">
          The Instagram app is not configured on this server. Set <code>INSTAGRAM_APP_ID</code>,{' '}
          <code>INSTAGRAM_APP_SECRET</code> and <code>INSTAGRAM_WEBHOOK_VERIFY_TOKEN</code>, restart
          the services, then point the Instagram webhook at <code>{cfg.webhookPath}</code>.
        </Alert>
      )}

      <Card
        title="Connected accounts"
        subtitle="Instagram professional accounts (Business or Creator)."
        actions={
          manage && cfg.configured ? (
            <Button
              variant="primary"
              size="sm"
              icon={<Camera size={14} />}
              loading={connecting}
              onClick={() => void startOauth()}
            >
              Connect with Instagram
            </Button>
          ) : undefined
        }
        flush
      >
        {list.length === 0 ? (
          <EmptyState icon={<Camera size={22} />} title="No Instagram account connected">
            Connect an account to receive its messages in the Inbox.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Status</th>
                  <th>Token</th>
                  <th>Connected</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="table__primary">@{a.username}</div>
                      <div className="table__secondary mono">{a.igUserId}</div>
                    </td>
                    <td>
                      <StatusBadge status={a.status} />
                      {a.status === 'needs_reauth' && (
                        <div className="table__secondary">Reconnect to keep receiving messages</div>
                      )}
                    </td>
                    <td>
                      {a.tokenExpiresAt ? (
                        <span>
                          Renews automatically · expires {formatRelative(a.tokenExpiresAt)}
                        </span>
                      ) : (
                        <span className="text-muted">Pasted token</span>
                      )}
                    </td>
                    <td>{formatRelative(a.createdAt)}</td>
                    <td className="table__actions">
                      {manage && a.status !== 'disconnected' && (
                        <Button
                          size="sm"
                          icon={<Trash2 size={14} />}
                          onClick={() => void disconnect(a)}
                          aria-label={`Disconnect @${a.username}`}
                        >
                          Disconnect
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {manage && cfg.configured && <TokenConnect onConnected={() => void accounts.reload()} />}

      <Card
        title="Automatic replies"
        subtitle="The first matching rule answers. Replies only go to people who commented or wrote to you; nothing is sent twice for the same comment."
        actions={
          manage && active.length > 0 ? (
            <Button size="sm" icon={<Plus size={14} />} onClick={() => setEditing('new')}>
              New rule
            </Button>
          ) : undefined
        }
      >
        {active.length === 0 ? (
          <EmptyState icon={<Camera size={22} />} title="Connect an account first">
            Rules belong to a connected Instagram account.
          </EmptyState>
        ) : (rules.data ?? []).length === 0 ? (
          <EmptyState icon={<Camera size={22} />} title="No rules yet">
            Example: when someone comments “price” on a post, send them the product link in a DM.
          </EmptyState>
        ) : (
          <div className="flow-grid">
            {(rules.data ?? []).map((r) => (
              <article key={r.id} className="rule-card">
                <header className="rule-card__head">
                  <h3 className="rule-card__name">{r.name}</h3>
                  <Badge tone={r.enabled ? 'success' : 'neutral'}>{r.enabled ? 'On' : 'Off'}</Badge>
                </header>
                <p className="rule-card__trigger">
                  {TRIGGER_LABEL[r.trigger]}
                  {r.mediaId ? ` · one post` : ''}
                </p>
                <p className="rule-card__keywords">
                  {r.keywords.length === 0 ? (
                    <span className="text-muted">Any text</span>
                  ) : (
                    r.keywords.map((k) => <Badge key={k}>{k}</Badge>)
                  )}
                </p>
                <p className="rule-card__reply">“{r.replyText}”</p>
                <div className="row">
                  {r.handoff && <Badge tone="warning">Then hands to a person</Badge>}
                  {r.commentReplyText && <Badge tone="info">Public reply too</Badge>}
                </div>
                {manage && (
                  <footer className="rule-card__actions">
                    <Button size="sm" onClick={() => void toggle(r)}>
                      {r.enabled ? 'Turn off' : 'Turn on'}
                    </Button>
                    <Button
                      size="sm"
                      icon={<Pencil size={14} />}
                      onClick={() => setEditing(r)}
                      aria-label={`Edit ${r.name}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      icon={<Trash2 size={14} />}
                      onClick={() => void remove(r)}
                      aria-label={`Delete ${r.name}`}
                    >
                      Delete
                    </Button>
                  </footer>
                )}
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card title="Recent rule activity" subtitle="The latest 50 automatic replies." flush>
        {(activity.data ?? []).length === 0 ? (
          <EmptyState icon={<Camera size={22} />} title="Nothing yet">
            Replies sent by your rules appear here.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Result</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {(activity.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td>{a.rule}</td>
                    <td>{OUTCOME_LABEL(a.outcome)}</td>
                    <td>{formatRelative(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Webhook setup" subtitle="Enter these in the Instagram product of your Meta app.">
        <div className="stack">
          <CopyField label="Callback URL" value={`${window.location.origin}${cfg.webhookPath}`} />
          {cfg.redirectUri && <CopyField label="OAuth redirect URI" value={cfg.redirectUri} />}
          <p className="field__hint">
            Subscribe to <code>messages</code> and <code>comments</code>. The verify token is the
            value of <code>INSTAGRAM_WEBHOOK_VERIFY_TOKEN</code>
            {cfg.webhookVerifyTokenSet ? ' (set)' : ' (not set yet)'}.
          </p>
        </div>
      </Card>

      {editing && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          accounts={active}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void rules.reload();
          }}
        />
      )}
    </div>
  );
}

function TokenConnect({ onConnected }: { onConnected: () => void }) {
  const toast = useToast();
  const submit = useSubmit();
  const [token, setToken] = useState('');
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.post('/instagram/accounts', { accessToken: token.trim() });
      return true;
    });
    if (ok) {
      setToken('');
      toast.success('Instagram account connected');
      onConnected();
    }
  };
  return (
    <Card
      title="Connect with an access token"
      subtitle="Use this if the login flow is not available. The token is verified with Instagram, then stored encrypted and never shown again."
    >
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Instagram user access token"
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          error={submit.fieldErrors['accessToken']}
          required
        />
        <div>
          <Button type="submit" loading={submit.pending}>
            Connect account
          </Button>
        </div>
      </form>
    </Card>
  );
}

function RuleModal({
  rule,
  accounts,
  onClose,
  onSaved,
}: {
  rule: Rule | null;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const submit = useSubmit();
  const [name, setName] = useState(rule?.name ?? '');
  const [accountId, setAccountId] = useState(rule?.accountId ?? accounts[0]?.id ?? '');
  const [trigger, setTrigger] = useState<Trigger>(rule?.trigger ?? 'comment');
  const [keywords, setKeywords] = useState((rule?.keywords ?? []).join(', '));
  const [matchType, setMatchType] = useState<'contains' | 'exact'>(rule?.matchType ?? 'contains');
  const [mediaId, setMediaId] = useState(rule?.mediaId ?? '');
  const [replyText, setReplyText] = useState(rule?.replyText ?? '');
  const [commentReply, setCommentReply] = useState(rule?.commentReplyText ?? '');
  const [handoff, setHandoff] = useState(rule?.handoff ?? false);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      const body = {
        accountId,
        name,
        trigger,
        keywords: keywords
          .split(/[,\n]/)
          .map((k) => k.trim())
          .filter(Boolean),
        matchType,
        mediaId: trigger === 'comment' && mediaId.trim() ? mediaId.trim() : null,
        replyText,
        commentReplyText: trigger === 'comment' && commentReply.trim() ? commentReply.trim() : null,
        handoff,
        enabled,
      };
      if (rule) await api.patch(`/instagram/rules/${rule.id}`, body);
      else await api.post('/instagram/rules', body);
      return true;
    });
    if (ok) onSaved();
  };

  return (
    <Modal open title={rule ? 'Edit rule' : 'New rule'} onClose={onClose}>
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={submit.fieldErrors['name']}
          maxLength={120}
          required
        />
        <div className="form-grid">
          <SelectField
            label="Account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            options={accounts.map((a) => ({ value: a.id, label: `@${a.username}` }))}
          />
          <SelectField
            label="When"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as Trigger)}
            options={(Object.keys(TRIGGER_LABEL) as Trigger[]).map((t) => ({
              value: t,
              label: TRIGGER_LABEL[t],
            }))}
          />
        </div>
        <TextField
          label="Keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          error={submit.fieldErrors['keywords']}
          hint={
            trigger === 'dm_keyword'
              ? 'Comma separated. Required: at least one keyword.'
              : 'Comma separated. Leave empty to answer every one.'
          }
        />
        <SelectField
          label="Match"
          value={matchType}
          onChange={(e) => setMatchType(e.target.value as typeof matchType)}
          options={[
            { value: 'contains', label: 'Text contains a keyword' },
            { value: 'exact', label: 'Text is exactly a keyword' },
          ]}
        />
        {trigger === 'comment' && (
          <TextField
            label="Only on one post (media id)"
            value={mediaId}
            onChange={(e) => setMediaId(e.target.value)}
            error={submit.fieldErrors['mediaId']}
            hint="Optional. Leave empty for every post. A post-specific rule wins over a catch-all."
            inputMode="numeric"
          />
        )}
        <TextAreaField
          label={trigger === 'comment' ? 'Private reply (sent as a DM)' : 'Reply'}
          rows={3}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          error={submit.fieldErrors['replyText']}
          maxLength={1000}
          required
        />
        {trigger === 'comment' && (
          <TextField
            label="Public reply under the comment"
            value={commentReply}
            onChange={(e) => setCommentReply(e.target.value)}
            error={submit.fieldErrors['commentReplyText']}
            hint="Optional, for example “Check your DMs 📩”."
            maxLength={300}
          />
        )}
        <Checkbox
          label="Then hand the conversation to a person"
          checked={handoff}
          onChange={(e) => setHandoff(e.target.checked)}
        />
        <Checkbox
          label="Rule is on"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submit.pending}>
            {rule ? 'Save rule' : 'Create rule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
